import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import { useCallback } from 'react';
import { Alert, Col, Modal, Row, Spinner } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { FailCodeSelection } from './fail-codes-modal.types';

import { useNoTroubleFound } from './hooks/use-no-trouble-found';

/**
 * Props for the {@link NoTroubleFound} component.
 */
export type NoTroubleFoundProps = {
    /** Close the modal entirely. */
    onClose: () => void;

    /** Callback invoked with the final fail code selection when the user confirms. */
    onConfirmFailCode: (selection: FailCodeSelection) => void;

    /** Navigate back to the search method selector. */
    onBack: () => void;
};

/**
 * Presentational component for the No Trouble Found fail code search method.
 * Renders a 3-level cascading typeahead (location -> frequency -> concern) and
 * delegates all state management and API interactions to {@link useNoTroubleFound}.
 *
 * @param props - {@link NoTroubleFoundProps}
 * @returns the no trouble found JSX
 */
const useNoTroubleFoundStyles = createUseStyles({
    buttonRow: {
        display: 'flex',
        gap: 15,
    },
});

/**
 * NoTroubleFound component renders the fail code search flow for 'No Trouble Found' cases.
 * Guides the user through location, frequency, and concern selection.
 *
 * @param props - NoTroubleFoundProps
 * @returns JSX.Element
 */
export function NoTroubleFound({ onBack, onClose, onConfirmFailCode }: NoTroubleFoundProps) {
    // Hooks
    const classes = useNoTroubleFoundStyles();
    const { t } = useTranslation();

    // Self-contained read-only exception: this hook performs only read-only lookups (locations,
    // frequencies, concerns). The parent does not participate in any intermediate cascade state.
    // The final output is delivered via the `onConfirmFailCode` callback.
    const noTroubleFound = useNoTroubleFound();

    // Handlers

    /**
     * Handle the back button click by navigating to the search method selector.
     */
    const handleBackButtonPress = useCallback(() => {
        onBack();
    }, [onBack]);

    /**
     * Handle the submit button click by building the final selection and passing
     * it to the parent, then closing the modal.
     */
    const handleSubmitButtonPress = useCallback(() => {
        onConfirmFailCode(noTroubleFound.confirmSelection());
        onClose();
    }, [onClose, onConfirmFailCode, noTroubleFound]);

    return (
        <>
            <Modal.Header closeButton>
                <h4 className="mb-0">{t('qmr:sections:failCode.noTroubleFound')}</h4>
            </Modal.Header>
            <Modal.Body>
                {noTroubleFound.error && (
                    <Alert show variant="danger">
                        {noTroubleFound.error}
                    </Alert>
                )}

                {noTroubleFound.isLoading ? (
                    <Spinner animation="border" />
                ) : (
                    <Row>
                        <Col className="mb-4" xs={12}>
                            <Label>{t('qmr:sections:failCode.specificVehicleLocation')}</Label>

                            <Typeahead
                                id="fail-code-no-trouble-modal-specific-vehicle__section"
                                onChange={noTroubleFound.handleLocationChange}
                                options={noTroubleFound.locationOptionData}
                                placeholder={t('qmr:sections:failCode.enterLetterCode')}
                                selected={noTroubleFound.selectedLocation}
                            />
                        </Col>

                        <Col className="mb-4" xs={12}>
                            <Label>{t('qmr:sections:failCode.howOftenConditionOccurs')}</Label>

                            <Typeahead
                                disabled={!noTroubleFound.selectedLocation.length}
                                id="fail-code-search-modal__specific_vehicle_location"
                                onChange={noTroubleFound.handleFrequencyChange}
                                options={noTroubleFound.frequencyOptionData}
                                placeholder={t('qmr:sections:failCode.enterLetterCode')}
                                selected={noTroubleFound.selectedFrequency}
                            />
                        </Col>

                        <Col className="mb-4" xs={12}>
                            <Label>{t('qmr:sections:failCode.symptomCondition')}</Label>

                            <Typeahead
                                disabled={!noTroubleFound.selectedFrequency.length}
                                id="fail-code-search-modal__repair-type"
                                onChange={noTroubleFound.handleConcernChange}
                                options={noTroubleFound.concernOptionData}
                                placeholder={t('qmr:sections:failCode.enterLetterCode')}
                                selected={noTroubleFound.selectedConcern}
                            />
                        </Col>
                    </Row>
                )}
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button
                    iconLeft={<Icon color="blueOne" name="chevron-left" />}
                    onPress={handleBackButtonPress}
                    variant="ghost-blue"
                >
                    {t('qmr:sections:failCode.back')}
                </Button>
                <div className={classes.buttonRow}>
                    <Button onPress={onClose} variant="link-blue">
                        <Typography color="blueOne">{t('qmr:sections:failCode.cancel')}</Typography>
                    </Button>
                    <Button disabled={!noTroubleFound.canConfirm} onPress={handleSubmitButtonPress}>
                        {t('qmr:sections:failCode.addFailCode')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}
